/**
 * Per-gate metadata for the 32 Tier-1 hard gates the protocol scorer
 * runs. Gate definitions mirror `scoring_bundle/scoring_module.md`.
 *
 * `WasmScorer.scoreVerbose` returns 256 `uint16` scorer outputs; the
 * first 32 entries are the gate results, with `65535` = pass and `0`
 * = fail. `failingGates(scorerOutputs)` extracts the failing indices.
 */

export const GATE_COUNT = 32;

/**
 * The "binary gate" encoding the WASM uses for slots 0-31: 65535 = pass,
 * anything else (in practice 0) = fail. Documented in `scoring_module.md`.
 */
const GATE_PASS_VALUE = 65535;

export interface GateInfo {
  /// Gate id, 0..31. Matches the index of the scorer output slot.
  readonly id: number;
  /// Short stable identifier from the protocol spec (e.g. `bip_presence`).
  readonly name: string;
  /// Plain-English hint for the player when this gate fails.
  readonly hint: string;
}

/// All 32 hard gates, indexed by id. Hints favor concrete suggestions
/// the player can act on; the gate's underlying condition (from the
/// spec) is in the comment above each entry.
export const GATES: readonly GateInfo[] = [
  // n_tokens >= 8
  {
    id: 0,
    name: "token_count_min",
    hint: "Your sentence is too short — aim for at least 8 encoded tokens (roughly 6-8 words).",
  },
  // n_tokens <= 40
  {
    id: 1,
    name: "token_count_max",
    hint: "Your sentence is too long — keep it under 40 encoded tokens (roughly 30 words).",
  },
  // Last token is in {., ?, !}
  {
    id: 2,
    name: "terminal_punctuation",
    hint: "End your sentence with a period, question mark, or exclamation mark.",
  },
  // First non-QUOTE_START token is CAP or ALLCAP
  {
    id: 3,
    name: "starts_uppercase",
    hint: "Start your sentence with a capital letter.",
  },
  // All token IDs in [0, 4095]
  {
    id: 4,
    name: "valid_encoding",
    hint: "The encoder produced an out-of-range token. Try rephrasing without unusual characters.",
  },
  // At most 1 terminal punctuation token
  {
    id: 5,
    name: "single_sentence",
    hint: "Stick to one sentence — only one terminal `.`, `?`, or `!` is allowed.",
  },
  // No run of 3+ consecutive control tokens
  {
    id: 6,
    name: "no_consecutive_controls",
    hint: "Avoid three or more control tokens in a row (e.g. consecutive ALL-CAPS or unusual quoting).",
  },
  // At least 2 tokens with cat_lookup[id] >= 2 (content words)
  {
    id: 7,
    name: "has_content_words",
    hint: "Use at least two content words (nouns, verbs, adjectives, etc.).",
  },
  // At least 1 of the 5 assigned BIP-39 words appears
  {
    id: 8,
    name: "bip_presence",
    hint: "Use at least one of today's blend words.",
  },
  // All 5 BIP-39 indices are distinct AND all 5 appear
  {
    id: 9,
    name: "bip_uniqueness",
    hint: "Use all five of today's blend words — each one needs to appear at least once.",
  },
  // Maximum consecutive run of assigned BIP-39 words <= 3
  {
    id: 10,
    name: "bip_no_cluster",
    hint: "Don't stack more than three blend words back-to-back; break them up with other words.",
  },
  // pos_tags[bip39[i]] in {1,2,3,4}
  {
    id: 11,
    name: "bip_pos_validity",
    hint: "One of the blend words is being parsed as the wrong part of speech — try using it more naturally in context.",
  },
  // Count of BIP-39 tokens <= 50% of n_tokens
  {
    id: 12,
    name: "bip_not_majority",
    hint: "Blend words shouldn't be more than half the sentence — surround them with other vocabulary.",
  },
  // No 3 consecutive identical tokens
  {
    id: 13,
    name: "no_triple_repeat",
    hint: "Don't repeat the same word three times in a row.",
  },
  // No single token appears more than 25% of the time
  {
    id: 14,
    name: "word_repetition_limit",
    hint: "No single word should make up more than a quarter of the sentence.",
  },
  // unique_bigrams / total_bigrams >= 0.5
  {
    id: 15,
    name: "bigram_diversity",
    hint: "Vary your word pairings — half or more of the adjacent word pairs need to be unique.",
  },
  // No repeating pattern of period 1-4 that repeats more than 3 times
  {
    id: 16,
    name: "no_cyclic_pattern",
    hint: "Avoid short repeating patterns (e.g. ABAB ABAB or ABCABC) more than three cycles long.",
  },
  // unique_trigrams / total_trigrams >= 0.4
  {
    id: 17,
    name: "trigram_diversity",
    hint: "Vary three-word runs more — too many trigrams are repeating.",
  },
  // At least 3 distinct content words
  {
    id: 18,
    name: "no_single_word_sentence",
    hint: "Use at least three different content words.",
  },
  // Average character length of word tokens in [2.0, 15.0]
  {
    id: 19,
    name: "reasonable_word_length",
    hint: "Mix word lengths — the average word length is currently too short or too long.",
  },
  // Token IDs are neither strictly increasing nor strictly decreasing
  {
    id: 20,
    name: "not_monotonic_ids",
    hint: "Your token IDs are running strictly in order — vary the word choices so they aren't all sequential in the vocabulary.",
  },
  // At least one token where pos_tags[id] == 2 (verb)
  {
    id: 21,
    name: "has_verb",
    hint: "Add a verb — your sentence needs at least one action word.",
  },
  // Content word ratio >= 0.3
  {
    id: 22,
    name: "not_semantic_null",
    hint: "At least 30% of the sentence needs to be content words — fewer function words like 'the' or 'of'.",
  },
  // At least 2 distinct non-zero frequency bands
  {
    id: 23,
    name: "frequency_band_spread",
    hint: "Mix common words with less common ones — your sentence is too narrow on word frequency.",
  },
  // At least one token with syllable_counts[id] > 1
  {
    id: 24,
    name: "syllable_variety",
    hint: "Use at least one multi-syllable word.",
  },
  // Control token count < max(n/4, 2)
  {
    id: 25,
    name: "control_token_limit",
    hint: "Too many control tokens (e.g. ALL-CAPS, quotes) — reduce them.",
  },
  // Unique tokens / n_tokens >= 0.3
  {
    id: 26,
    name: "min_unique_ratio",
    hint: "Use more distinct words — at least 30% of tokens need to be unique.",
  },
  // Punctuation ratio < 0.3
  {
    id: 27,
    name: "no_excessive_punctuation",
    hint: "Cut some of the punctuation — fewer than 30% of tokens should be punctuation.",
  },
  // 8 <= n_tokens <= 40 (redundant with 0+1)
  {
    id: 28,
    name: "reasonable_sentence_length",
    hint: "Sentence length is outside the 8-40 token range.",
  },
  // Average absolute token ID difference < 2000
  {
    id: 29,
    name: "not_random_tokens",
    hint: "The word choices look too random for the encoder — try a more naturally-phrased sentence.",
  },
  // At least 3 distinct POS tag values
  {
    id: 30,
    name: "has_multiple_pos",
    hint: "Mix more parts of speech — use a combination of nouns, verbs, and modifiers.",
  },
  // Every CAP/ALLCAP is followed by a word token; NO_SPACE is not final
  {
    id: 31,
    name: "no_control_waste",
    hint: "Capitalization or spacing is being used without an attached word — review where you have CAPS or unusual joining.",
  },
] as const;

/**
 * Returns the ids of gates that failed. `scorerOutputs` is what
 * `WasmScorer.scoreVerbose` produces; slots 0..31 hold the gate bits.
 */
export function failingGates(scorerOutputs: Uint16Array): readonly GateInfo[] {
  if (scorerOutputs.length < GATE_COUNT) {
    throw new Error(
      `expected at least ${GATE_COUNT.toString()} scorer outputs; got ${scorerOutputs.length.toString()}`,
    );
  }
  const failed: GateInfo[] = [];
  for (let i = 0; i < GATE_COUNT; i++) {
    if (scorerOutputs[i] !== GATE_PASS_VALUE) {
      failed.push(GATES[i]!);
    }
  }
  return failed;
}
